import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Popconfirm, Tag, Checkbox, Avatar, Tooltip, Popover, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, EnterOutlined } from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import useTodoStore from '../../stores/todoStore';
import useEmployeeStore from '../../stores/employeeStore';
import moment from 'moment';

const Todos = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quickInput, setQuickInput] = useState('');

  const { todos, loading, fetchTodos, createTodo, updateTodo, deleteTodo, toggleTodoStatus } = useTodoStore();
  const { employees, fetchEmployees } = useEmployeeStore();

  useEffect(() => {
    fetchTodos();
    fetchEmployees();
  }, [fetchTodos, fetchEmployees]);

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const AssigneeSelector = ({ record, assignee }) => {
    const handleAssigneeChange = async (newAssigneeId) => {
      const newAssignee = employees.find(emp => emp.id === newAssigneeId);
      const originalAssignee = assignee;
      
      // Optimistic update
      updateTodo(record.id, {
        ...record,
        assignee: newAssigneeId || null,
        assignee_details: newAssignee || null
      });
    };

    return (
      <Popover
        destroyTooltipOnHide
        trigger="click"
        content={
          <Select
            style={{ width: 200 }}
            placeholder="Select assignee"
            value={assignee?.id || null}
            onChange={handleAssigneeChange}
            allowClear
            options={employees.map(emp => ({
              value: emp.id,
              label: emp.name,
            }))}
            onClick={e => e.stopPropagation()}
          />
        }
      >
        <Tooltip>
          {assignee ? (
            <Avatar 
              style={{ 
                backgroundColor: '#1677ff',
                verticalAlign: 'middle',
                cursor: 'pointer'
              }}
              size="small"
            >
              {getInitials(assignee.name)}
            </Avatar>
          ) : (
            <Avatar 
              style={{ 
                backgroundColor: '#d9d9d9',
                verticalAlign: 'middle',
                cursor: 'pointer'
              }}
              size="small"
            >
              N/A
            </Avatar>
          )}
        </Tooltip>
      </Popover>
    );
  };

  const columns = [
    {
      title: '',
      dataIndex: 'completed',
      key: 'completed',
      width: 90,
      fixed: 'left',
      render: (completed, record) => (
        <Space size="small">
          <Checkbox
            checked={completed}
            onChange={() => toggleTodoStatus(record.id)}
          />
          <Button 
            type="text"
            icon={<EditOutlined />} 
            onClick={() => showModal(record)}
          />
        </Space>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: {
        showTitle: false
      },
      render: (description) => (
        <Tooltip 
          placement="topLeft" 
          //title={description || 'No description'}
          overlayStyle={{ maxWidth: '500px' }}
        >
          <span style={{ 
            color: description ? 'rgba(0, 0, 0, 0.88)' : '#00000040',
            fontStyle: 'italic'
          }}>
            {description || 'No description'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '',
      dataIndex: 'assignee_details',
      key: 'assignee',
      width: 70, // Reduced width since we only show avatar now
      render: (assignee, record) => (
        <AssigneeSelector record={record} assignee={assignee} />
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 150,
      render: (date) => {
        if (!date) return null;
        const momentDate = moment(date);
        return (
          <Tooltip title={momentDate.format('YYYY-MM-DD HH:mm:ss')}>
            {momentDate.format('YYYY-MM-DD')}
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'delete',
      width: 50,
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Delete Todo"
          description="Are you sure?"
          onConfirm={() => deleteTodo(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button 
            type="text"
            danger 
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      ),
    },
  ];

  const showModal = (todo = null) => {
    setEditingTodo(todo);
    if (todo) {
      const formData = { ...todo };
      // Convert ISO string to moment object for DatePicker
      if (todo.due_date) {
        formData.due_date = moment(todo.due_date);
      }
      form.setFieldsValue(formData);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    try {
      const result = editingTodo 
        ? await updateTodo(editingTodo.id, values)
        : await createTodo(values);
      
      if (result.success) {
        setIsModalVisible(false);
        form.resetFields();
        setEditingTodo(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickAdd = async () => {
    if (!quickInput.trim()) return;
    
    const todoData = { title: quickInput.trim() };
    setQuickInput(''); // Clear input immediately for better UX
    const result = await createTodo(todoData);
    
    // If creation failed, restore the input value
    if (!result.success) {
      setQuickInput(todoData.title);
    }
  };

  const sortTodos = (todos) => {
    return [...todos].sort((a, b) => {
      // Get status for both todos
      const statusA = a.completed ? 'completed' : getDueStatus(a.due_date) || 'normal';
      const statusB = b.completed ? 'completed' : getDueStatus(b.due_date) || 'normal';
      
      // Define priority order
      const priority = {
        'overdue': 0,
        'due-today': 1,
        'normal': 2,
        'completed': 3
      };
      
      // Sort by priority
      return priority[statusA] - priority[statusB];
    });
  };

  const getDueStatus = (dueDate) => {
    if (!dueDate) return null;
    const today = moment().startOf('day');
    const due = moment(dueDate).startOf('day');
    
    if (due.isSame(today)) {
      return 'due-today';
    } else if (due.isBefore(today)) {
      return 'overdue';
    }
    return null;
  };

  return (
    <AdminPageLayout
      title="Todos"
      icon={<CheckOutlined />}
      subtitle="Manage your todos"
      actions={
        <Space.Compact style={{ width: '300px' }}>
          <Input
            placeholder="Add a quick todo..."
            value={quickInput}
            onChange={(e) => setQuickInput(e.target.value)}
            onPressEnter={handleQuickAdd}
            disabled={submitting}
          />
          <Button 
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleQuickAdd}
            loading={submitting}
          />
        </Space.Compact>
      }
    >
      <Table
        columns={columns}
        dataSource={sortTodos(todos)}
        loading={loading}
        rowKey="id"
        size="small"
        scroll={{
          x: 800,
          y: 'calc(100vh - 280px)',
        }}
        pagination={{
          size: 'small',
          pageSize: 50,
        }}
        rowClassName={(record) => {
          const classes = [];
          const dueStatus = getDueStatus(record.due_date);
          if (dueStatus) {
            classes.push(dueStatus);
          }
          if (record.completed) {
            classes.push('completed-todo');
          }
          return classes.join(' ');
        }}
      />

      <Modal
        title={editingTodo ? "Edit Todo" : "Add Todo"}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingTodo(null);
        }}
        confirmLoading={submitting}
        okText={submitting ? "Saving..." : "Save"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please input the title!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="assignee"
            label="Assignee"
          >
            <Select
              placeholder="Select assignee"
              allowClear
              options={employees.map(emp => ({
                value: emp.id,
                label: emp.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="due_date"
            label="Due Date"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder="Select due date"
              allowClear
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="completed"
            valuePropName="checked"
          >
            <Checkbox>Mark as completed</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </AdminPageLayout>
  );
};

const styles = document.createElement('style');
styles.innerHTML = `
  .ant-table-row td {
    background-color: #ffffff !important;
  }
  .ant-table-row:hover td {
    background-color: #f5f5f5 !important;
  }
  .overdue td {
    background-color: #fff2f0 !important;
  }
  .overdue:hover td {
    background-color: #ffccc7 !important;
  }
  .due-today td {
    background-color: #fffbe6 !important;
  }
  .due-today:hover td {
    background-color: #fff1b8 !important;
  }
  /* Always apply completed style last to take precedence */
  .completed-todo td {
    background-color: #f6ffed !important;
  }
  .completed-todo:hover td {
    background-color: #e8f7d6 !important;
  }
`;
document.head.appendChild(styles);

export default Todos;
